import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  Progress,
  Row,
  Space,
  Statistic,
  Typography,
} from "antd";
import {
  CloudUploadOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { getGameTags } from "../../../services/gameTags/gameTags.service";
import { getApiErrorMessage } from "../../../utils";
import ChangedInPlatformModal from "./ChangedInPlatformModal";
import { getHubRows } from "./contentHub.service";
import { lockRefusalMessage, lockRequestFor, takeLock } from "./hubLock";
import type { HeldLock } from "./hubLock";
import { runHub } from "./hubRunner";
import type { RunMode } from "./hubRunner";
import type { ChangedAnswer, ChangedPrompt, HubRow, RowRun } from "./hubTypes";
import { findQuizzesWithRecords } from "./quizImpactCheck";
import type { QuizWithRecords } from "./quizImpactCheck";
import QuizzesWithRecordsModal from "./QuizzesWithRecordsModal";
import RowsTable from "./RowsTable";
import type { RowItem } from "./RowsTable";
import { parseRowSelection, selectRows } from "./rowSelection";
import { rowView, summarizeRows } from "./rowState";
import type { PartCounts } from "./rowState";
import { RELOAD_STEP, runReport } from "./runReport";

const { Text } = Typography;

const NO_ROWS: HubRow[] = [];
const LOST_LEASE_MESSAGE = `Se perdió el turno de este tracker y la carga se detuvo. ${RELOAD_STEP}`;

interface LoadedRows {
  rows: HubRow[];
  // As the backend resolved them: rows are written back to the same tab.
  source: { spreadsheet: string; sheet: string };
  // «Volver a subir lo ya subido» as it was when the rows were loaded: it
  // decides which rows came back and what the run does with them.
  reupload: boolean;
}

const NEEDS_ROWS_MESSAGE =
  "Para volver a subir lo ya subido, indica en «Filas» qué filas.";
const NEEDS_RELOAD_MESSAGE =
  "Toca «Cargar filas» para aplicar «Volver a subir lo ya subido».";
const QUIZ_RECORDS_CHECK_FAILED =
  "No se pudo revisar si los quizzes tienen tareas o intentos de alumnos. No se subió nada.";

const PartCounters: React.FC<{ title: string; counts: PartCounts }> = ({
  title,
  counts,
}) => (
  <Space size="large" align="center">
    <Text strong>{title}</Text>
    <Statistic title="Pendientes" value={counts.pending} />
    <Statistic title="Subidos" value={counts.uploaded} />
    <Statistic title="Con error" value={counts.withError} />
  </Space>
);

const Subidas: React.FC = () => {
  // Both start empty: a copy of the tracker must never be mistaken for the real one.
  const [tracker, setTracker] = useState("");
  const [sheet, setSheet] = useState("");
  const [rowsText, setRowsText] = useState("");
  const [reupload, setReupload] = useState(false);
  const [loaded, setLoaded] = useState<LoadedRows | null>(null);
  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState<Record<number, RowRun>>({});
  const [mode, setMode] = useState<RunMode | null>(null);
  const [stopping, setStopping] = useState(false);
  // An upload changes what the platform holds for these rows: processing the
  // same copy again could overwrite a good record in the tracker.
  const [needsReload, setNeedsReload] = useState(false);
  // What the last run did, until the next run starts or the rows are loaded.
  const [report, setReport] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stopRequested = useRef(false);
  const heldLock = useRef<HeldLock | null>(null);
  // The runner asks one question at a time: a course changed in the platform.
  const [question, setQuestion] = useState<ChangedPrompt | null>(null);
  const answerQuestion = useRef<((answer: ChangedAnswer) => void) | null>(null);
  // SCRUM-1581: and an upload asks once, before it starts, about the quizzes
  // homework or attempts already reach.
  const [quizzesToConfirm, setQuizzesToConfirm] = useState<
    QuizWithRecords[] | null
  >(null);
  const answerQuizzes = useRef<((confirmed: boolean) => void) | null>(null);
  const running = mode !== null;

  // Leaving the page stops new rows from starting, the ones in flight finish,
  // and the tracker is handed back. An open question is cancelled, so the
  // part it holds can end.
  useEffect(
    () => () => {
      stopRequested.current = true;
      heldLock.current?.release();
      answerQuestion.current?.("cancel");
      answerQuestion.current = null;
      answerQuizzes.current?.(false);
      answerQuizzes.current = null;
    },
    [],
  );

  const confirmChanged = (prompt: ChangedPrompt) =>
    new Promise<ChangedAnswer>((resolve) => {
      answerQuestion.current = resolve;
      setQuestion(prompt);
    });

  const handleAnswer = (answer: ChangedAnswer) => {
    const resolve = answerQuestion.current;
    answerQuestion.current = null;
    setQuestion(null);
    resolve?.(answer);
  };

  const handleQuizzesAnswer = (confirmed: boolean) => {
    const resolve = answerQuizzes.current;
    answerQuizzes.current = null;
    setQuizzesToConfirm(null);
    resolve?.(confirmed);
  };

  // The quizzes the upload may replace over what reaches them, or null when it
  // must not start: the person cancelled, the check failed, or the run was
  // stopped meanwhile (Detener, a lost turn, the page left).
  const confirmQuizzesWithRecords = async (
    runRows: HubRow[],
    runReupload: boolean,
  ): Promise<Set<number> | null> => {
    let quizzes: QuizWithRecords[];
    try {
      quizzes = await findQuizzesWithRecords(runRows, {
        reupload: runReupload,
      });
    } catch {
      setError(QUIZ_RECORDS_CHECK_FAILED);
      return null;
    }
    if (stopRequested.current) return null;
    if (quizzes.length === 0) return new Set();
    const confirmed = await new Promise<boolean>((resolve) => {
      answerQuizzes.current = resolve;
      setQuizzesToConfirm(quizzes);
    });
    if (!confirmed || stopRequested.current) return null;
    return new Set(quizzes.map((quiz) => quiz.gameQuizId));
  };

  useEffect(() => {
    if (!running) return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [running]);

  const rows = loaded?.rows ?? NO_ROWS;
  const selection = useMemo(() => parseRowSelection(rowsText), [rowsText]);
  const selectedRows = useMemo(
    () => (selection.ok ? selectRows(rows, selection.ranges) : NO_ROWS),
    [rows, selection],
  );
  // A selection narrows the table, the counters and the runs alike; text that
  // cannot be read narrows nothing, so every row stays in view.
  const shownRows = selection.ok ? selectedRows : rows;
  const loadedReupload = loaded?.reupload ?? false;
  const items = useMemo<RowItem[]>(
    () =>
      shownRows.map((row) => {
        const run = runs[row.rowNumber];
        return {
          row,
          run,
          view: rowView(row, run, { reupload: loadedReupload }),
        };
      }),
    [shownRows, runs, loadedReupload],
  );
  const summary = useMemo(
    () => summarizeRows(items.map((item) => item.view)),
    [items],
  );
  const processed = items.filter((item) => item.run?.messages !== undefined);
  const restricted = selection.ok && selection.ranges !== null;
  // Uploading a whole tab again by mistake would replace every course in it.
  const needsRows = loadedReupload && !restricted;
  const staleReupload = loaded !== null && loaded.reupload !== reupload;
  const canRun =
    !running &&
    !needsReload &&
    !needsRows &&
    !staleReupload &&
    selectedRows.length > 0;

  const handleLoad = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getHubRows(tracker.trim(), sheet.trim(), reupload);
      setLoaded({
        rows: response.rows,
        source: { spreadsheet: response.spreadsheetId, sheet: response.sheet },
        reupload,
      });
      setRuns({});
      setReport(null);
      setNeedsReload(false);
    } catch (err) {
      setError(
        getApiErrorMessage(err, "No se pudieron cargar las filas del tracker."),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRun = async (runMode: RunMode) => {
    if (!loaded) return;
    stopRequested.current = false;
    setStopping(false);
    setError(null);
    setReport(null);
    setRuns({});
    setMode(runMode);

    // A run that cannot take the rows and courses it works on does not start.
    const request = lockRequestFor(
      loaded.source.spreadsheet,
      selectedRows,
      restricted,
    );
    let leaseLost = false;
    let lock: HeldLock;
    try {
      lock = await takeLock(request, () => {
        leaseLost = true;
        stopRequested.current = true;
        setStopping(true);
        setError(LOST_LEASE_MESSAGE);
      });
    } catch (err) {
      setError(
        lockRefusalMessage(err, request.rows) ??
          getApiErrorMessage(err, "No se pudo tomar el turno de este tracker."),
      );
      setMode(null);
      return;
    }
    heldLock.current = lock;

    let confirmedQuizIds = new Set<number>();
    if (runMode === "upload") {
      const confirmed = await confirmQuizzesWithRecords(
        selectedRows,
        loaded.reupload,
      );
      if (confirmed === null) {
        lock.release();
        heldLock.current = null;
        setMode(null);
        return;
      }
      confirmedQuizIds = confirmed;
    }

    const results: Record<number, RowRun> = {};
    let failure: string | null = null;
    try {
      const [blooms, pisa] = await Promise.all([
        getGameTags("BLOOMS"),
        getGameTags("PISA"),
      ]);
      const result = await runHub({
        mode: runMode,
        rows: selectedRows,
        source: loaded.source,
        tagDefs: [...blooms, ...pisa],
        onUpdate: (rowNumber, patch) => {
          results[rowNumber] = { ...results[rowNumber], ...patch };
          setRuns({ ...results });
        },
        shouldStop: () => stopRequested.current,
        confirmChanged,
        confirmedQuizIds,
        reupload: loaded.reupload,
      });
      failure = result.fatal;
    } catch (err) {
      failure = getApiErrorMessage(
        err,
        "La carga se interrumpió por un error inesperado.",
      );
    } finally {
      lock.release();
      heldLock.current = null;
      if (runMode === "upload" || leaseLost) setNeedsReload(true);
      setMode(null);
    }
    if (failure && !leaseLost) setError(failure);
    // A lost lease says what to do in its own message.
    if (!leaseLost) {
      setReport(
        runReport({
          mode: runMode,
          stopped: stopRequested.current || failure !== null,
          rows: selectedRows,
          runs: results,
          reupload: loaded.reupload,
        }),
      );
    }
  };

  const handleStop = () => {
    stopRequested.current = true;
    setStopping(true);
  };

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} md={10}>
            <label htmlFor="hub-tracker">
              <Text strong>Tracker</Text>
            </label>
            <Input
              id="hub-tracker"
              value={tracker}
              onChange={(e) => setTracker(e.target.value)}
              placeholder="URL o ID de la hoja de cálculo"
              disabled={running}
            />
          </Col>
          <Col xs={24} md={5}>
            <label htmlFor="hub-sheet">
              <Text strong>Pestaña</Text>
            </label>
            <Input
              id="hub-sheet"
              value={sheet}
              onChange={(e) => setSheet(e.target.value)}
              placeholder="Nombre exacto de la pestaña"
              disabled={running}
            />
          </Col>
          <Col xs={24} md={5}>
            <label htmlFor="hub-rows">
              <Text strong>Filas</Text>
            </label>
            <Input
              id="hub-rows"
              value={rowsText}
              onChange={(e) => setRowsText(e.target.value)}
              placeholder="12, 15, 40-45"
              status={selection.ok ? undefined : "error"}
              disabled={running}
            />
            <Checkbox
              checked={reupload}
              disabled={running}
              onChange={(event) => setReupload(event.target.checked)}
              style={{ marginTop: 8 }}
            >
              Volver a subir lo ya subido
            </Checkbox>
          </Col>
          <Col xs={24} md={4}>
            <Button
              block
              loading={loading}
              disabled={running || !tracker.trim() || !sheet.trim()}
              onClick={handleLoad}
            >
              Cargar filas
            </Button>
          </Col>
        </Row>
        {selection.ok ? null : (
          <Text type="danger" style={{ display: "block", marginTop: 8 }}>
            {selection.error}
          </Text>
        )}
        {loaded && restricted ? (
          <Text
            type={selectedRows.length > 0 ? "secondary" : "warning"}
            style={{ display: "block", marginTop: 8 }}
          >
            {selectedRows.length > 0
              ? `Filas a procesar: ${selectedRows.length} de ${loaded.rows.length}.`
              : "Ninguna de las filas indicadas está entre las pendientes."}
          </Text>
        ) : null}
        {staleReupload ? (
          <Text type="warning" style={{ display: "block", marginTop: 8 }}>
            {NEEDS_RELOAD_MESSAGE}
          </Text>
        ) : needsRows ? (
          <Text type="warning" style={{ display: "block", marginTop: 8 }}>
            {NEEDS_ROWS_MESSAGE}
          </Text>
        ) : null}
      </Card>

      {running ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="No cierres ni abandones esta página mientras se procesan las filas. Si sales, la carga se detiene y las filas en curso no se registran en el tracker."
        />
      ) : null}
      {report ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={report}
        />
      ) : null}
      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16, whiteSpace: "pre-line" }}
          message={error}
        />
      ) : null}

      {loaded ? (
        <>
          <Card style={{ marginBottom: 16 }}>
            <Space
              wrap
              size="large"
              align="center"
              style={{ width: "100%", justifyContent: "space-between" }}
            >
              <Space size="large" wrap>
                <PartCounters title="OD" counts={summary.doc} />
                <PartCounters title="Quiz" counts={summary.quiz} />
              </Space>
              <Space wrap>
                <Button
                  icon={<SafetyCertificateOutlined />}
                  loading={mode === "validate"}
                  disabled={!canRun}
                  onClick={() => handleRun("validate")}
                >
                  Validar
                </Button>
                <Button
                  type="primary"
                  icon={<CloudUploadOutlined />}
                  loading={mode === "upload"}
                  disabled={!canRun}
                  onClick={() => handleRun("upload")}
                >
                  Subir pendientes
                </Button>
                {running ? (
                  <Button
                    danger
                    icon={<StopOutlined />}
                    disabled={stopping}
                    onClick={handleStop}
                  >
                    {stopping ? "Deteniendo…" : "Detener"}
                  </Button>
                ) : null}
              </Space>
            </Space>
            {running ? (
              <Progress
                style={{ marginTop: 16 }}
                percent={Math.round(
                  (processed.length / selectedRows.length) * 100,
                )}
                format={() =>
                  `${processed.length} de ${selectedRows.length} filas`
                }
              />
            ) : null}
          </Card>
          <RowsTable
            items={items}
            loading={loading}
            emptyText={
              restricted ? "Ninguna fila coincide con la selección." : undefined
            }
          />
        </>
      ) : null}
      <ChangedInPlatformModal prompt={question} onAnswer={handleAnswer} />
      <QuizzesWithRecordsModal
        quizzes={quizzesToConfirm}
        onConfirm={() => handleQuizzesAnswer(true)}
        onCancel={() => handleQuizzesAnswer(false)}
      />
    </>
  );
};

export default Subidas;
